import {Component, Input, SimpleChanges} from '@angular/core';
import * as d3 from 'd3';
import * as uuid from 'uuid'
import {LineChartDataModel} from '../../../../../models/line-chart-data.model';
import moment from 'moment';
import {HeightFormatPipe} from '../../../pipes/height-format.pipe';
import {CommonService} from '../../../services/common.service';

@Component({
  selector: 'line-chart',
  templateUrl: './line-chart.component.html',
  styleUrls: ['./line-chart.component.css']
})
export class LineChartComponent {
  @Input() chartName: string;
  @Input() chartData: LineChartDataModel;
  @Input() yAxisLabel: string;
  @Input() xAxisLabel: string;

  chartId = 'x' + uuid.v4();

  chatIdSelector = '#' + this.chartId

  svg: any;
  originalMargins = {top: 2, right: 2, bottom: 4, left: 5};
  margins = {top: 2, right: 2, bottom: 4, left: 5};
  width = 100;
  height = 100;

  legend = [];
  resizeObserver: ResizeObserver;

  yRange = [];

  constructor(
    private heightAndInches: HeightFormatPipe,
    private commonService: CommonService
  ) {
    // creating resize observer for detecting size change of chart container
    this.resizeObserver = new ResizeObserver(() => {
      this.renderChart();
    })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['chartData']?.currentValue) {
      this.chartData = JSON.parse(JSON.stringify(changes['chartData']?.currentValue));
      if (this.chartName === 'height-chart') {
        this.chartData.lines = this.chartData.lines.map((l: any) => {
          l.data = l.data.map(o => {
            o = JSON.parse(JSON.stringify(o));
            const r = Math.round(o.y * 100) / 100;

            const f = Math.floor(r);
            const i = (o.y - f) * 12;

            o.y = Math.round((f * 12 + i) * 100) / 100;

            return o;
          })

          return l;
        });
      }

      this.renderChart();
    }
  }

  ngOnInit() {
    setTimeout(() => {
      const chartContainer = document.getElementById(this.chartId);
      this.resizeObserver.observe(chartContainer);
    });
  }

  renderChart() {
    if (this.chartData) {
      setTimeout(() => {
        this.createChart();
      })
    }
  }

  createChart() {
    this.width = $(this.chatIdSelector).width();
    this.height = $(this.chatIdSelector).height();
    this.legend = [];
    this.margins.top = this.commonService.convertRemToPx(this.originalMargins.top);
    this.margins.right = this.commonService.convertRemToPx(this.originalMargins.right);
    this.margins.left = this.commonService.convertRemToPx(this.originalMargins.left);
    this.margins.bottom = this.commonService.convertRemToPx(this.originalMargins.bottom);

    const element = document.getElementById(this.chartId);
    if (!element) {
      // component destroyed but ngOnDestroy event is not triggered, so manually triggering
      // NOTE: This is happening when switching between XS <=> SM
      this.ngOnDestroy();
      return
    }
    element.innerHTML = '';

    // ********** DRAWING THE SVG ON SCREEN & ADJUSTING OR SETTING VIEW-BOX WIDTH AND HEIGHT **********//
    this.svg = d3.select(this.chatIdSelector)
      .append('svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `0, 0, ${this.width}, ${this.height}`)
      .attr('preserveAspectRatio', 'xMinYMin')
      .append('g')

    // Extract min max config from the values
    const minMaxConfig = this.getMinMaxValue({
      x: this.chartData.config.x.type,
      y: this.chartData.config.y.type
    });
    // ********** Create the X-axis band scale ********** //
    const x = minMaxConfig.x;


    // ********** Create the Y-axis band scale ********** //
    const y = minMaxConfig.y;


    // ********** Create tooltip ********** //
    const tooltip = d3.select(this.chatIdSelector)
      .append('div')
      .attr('class', 'tooltip')
      .style('position', 'fixed')
      .style('visibility', 'hidden')

    // Draw zone lines
    if (this.chartData.zones) {
      this.chartData.zones.forEach((item) => {
        const zonePoints = [
          {
            x: item.startX,
            y: item.startY
          },
          {
            x: item.endX,
            y: item.endY
          }
        ];
        this.svg.append('path')
          .datum(zonePoints)
          .attr('class', 'line')
          .attr('d', d3.area()
            .x((d: any) => x(new Date(d.x)))
            .y0(y(0))
            .y1((d: any) => y(d.y))
          )
          .style('fill', item.color);
      });
    }

    // draw lines
    this.chartData.lines.forEach((item) => {
      // ********** Create Line ********** //
      const line = d3.line()
        .x((d: any) => x(new Date(d.x)))
        .y((d: any) => y(d.y))


      // Add Y Axis Label
      this.svg.append('text')
        .attr('class', 'y-label')
        .attr('transform', 'rotate(-90)')
        .attr('y', 0)
        .attr('x', 0 - (this.height / 2))
        .attr('dy', '1em')
        .style('text-anchor', 'middle')
        .text(this.yAxisLabel);

      this.svg.append('path')
        .datum(item.data)
        .attr('class', `${item.lineId}`)
        .attr('d', line)
        .style('fill', 'none')
        .style('stroke', item.color)
        .style('stroke-width', '2')
        .attr('id', `${item.lineId}`)

      this.svg.append('g')
        .selectAll('dot')
        .data(item.data)
        .enter()
        .append('circle')
        .attr('class', `${item.lineId}_circle`)
        .attr('cx', (d) => x(new Date(d.x)))
        .attr('cy', (d) => y(d.y))
        .attr('r', 6)
        .style('fill', (d) => d.imputed ? (item.imputedColor || '#CCCCCC') : item.color)
        .style('stroke', () => 'none')
        .style('stroke-width', () => 0)
        .on('mouseover', (event: any, d) => {
          let yValue = ''
          if (this.chartName === 'height-chart') {
            yValue = this.heightAndInches.transform(d.y)
          } else {
            yValue = d.y
          }

          let unit = '';
          if (this.chartName === 'weight-chart') {
            unit = 'Pounds';
          } else if (this.chartName === 'bmi-chart') {
            unit = this.yAxisLabel;
          }
          return tooltip
            .html(`
              ${d.imputed ? `Estimated Height<br>` : ``}
              ${moment(d.x).format('MMM DD, YYYY')}<br>
              ${yValue} ${unit}
            `)
            .transition()
            .duration(200)
            .style('top', (event.clientY - (d.imputed ? 75 : 60)) + 'px')
            .style('left', this.commonService.calculateXForPopup(tooltip, event.clientX) + 'px')
            .style('visibility', 'visible');
        })
        .on('mousemove', (event: any, d) => {
          return tooltip
            .transition()
            .duration(0)
            .style('top', (event.clientY - (d.imputed ? 75 : 60)) + 'px')
            .style('left', this.commonService.calculateXForPopup(tooltip, event.clientX) + 'px');
        })
        .on('mouseout', () => {
          return tooltip
            .transition()
            .duration(0)
            .style('visibility', 'hidden');
        });

      // collect legend params
      this.legend.push({
        name: item.label,
        color: item.color,
        id: `${item.lineId}`
      })
    })

    // Add legends
    if (this.chartData.legend) {
      const legendItem = this.svg.selectAll('legendItem')
        .data(this.legend)
        .enter()
        .append('g')
        .attr('id', (d) => d.id)
        .attr('transform', (
          d,
          i
        ) => `translate(${this.width - 10}, ${((this.height / 2) - (this.legend.length * 12.5)) + i * 25})`)
        .style('cursor', 'pointer')
        .on('click', (event) => {
          const lineId = event.target.parentElement.id;
          this.toggleLine(lineId);
        })

      legendItem.append('circle')
        .attr('r', 7)
        .style('fill', (d) => d.color)

      legendItem.append('text')
        .style('fill', (d) => d.color)
        .text(function (d) {
          return d.name
        })
        .attr('x', '12px')
        .attr('y', '1px')
        .attr('text-anchor', 'left')
        .style('alignment-baseline', 'middle')
        .style('font-size', '15px')
    }

    this.svg.append('rect')
      .attr('x', -10)
      .attr('y', 0 - this.margins.bottom)
      .attr('width', this.width + 30)
      .attr('height', 500)
      .attr('fill', '#FFFFFF')
      .attr('transform', 'translate(-10,' + this.height + ')');

    // ********** Draw the Y-axis on the DOM ********** //
    if (this.chartName === 'height-chart') {
      const tickValues = d3.range(y.domain()[0], y.domain()[1], 1);

      this.svg.append('g')
        .attr('transform', `translate(${this.margins.left},0)`)
        .call(
          d3.axisLeft(y)
            .tickValues(tickValues)
            .tickFormat((d) => {
              const feet = Math.floor(Number(d) / 12);
              const inches = Math.round(((Number(d) / 12) - feet) * 12);
              return `${feet}' ${inches}"`;
            })
        );
    } else {
      this.svg.append('g')
        .attr('transform', `translate(${this.margins.left},0)`)
        .call(d3.axisLeft(y));
    }

    // ********** Draw the X-axis on the DOM ********** //
    const xAxisConfig = d3.axisBottom(x).tickFormat(d3.utcFormat('%m/%d'));
    if (this.width < 400) {
      xAxisConfig.ticks(5);
    }

    this.svg.append('g')
      .attr('class', 'axis')
      .attr('transform', `translate(0,${this.height - this.margins.bottom})`)
      .call(xAxisConfig)
      .selectAll('text')
      .style('text-anchor', 'center')
      .style('font-size', '1.2rem')

    // Add X Axis Label
    this.svg.append('text')
      .attr('class', 'x-label')
      .attr('x', this.width / 2)
      .attr('y', this.height)
      .style('text-anchor', 'middle')
      .text(this.xAxisLabel);
  }

  toggleLine(lineId: string) {
    if (d3.select(`#${lineId}`).attr('is_hidden') === '1') {
      d3.select(`#${lineId}`).attr('is_hidden', '0')
      // hide dot circle
      d3.selectAll(`.${lineId}_circle`).style('display', 'block')
      // hide line
      d3.selectAll(`.${lineId}`).style('display', 'block')
    } else {
      d3.select(`#${lineId}`).attr('is_hidden', '1')
      // hide dot circle
      d3.selectAll(`.${lineId}_circle`).style('display', 'none')
      // hide line
      d3.selectAll(`.${lineId}`).style('display', 'none')
    }
  }

  getMinMaxValue(type: {
    x: string,
    y: string,
  }) {
    const domain = {
      x: null,
      y: null,
      config: {
        minX: null,
        maxX: null,
        minY: null,
        maxY: null
      }
    }

    // collect all value into one bucket for calculating min and max values
    let wholesomeData = [];

    // add zone Y values to ensure shading is fully visible
    if (this.chartData.zones && this.chartData.zones.length > 0) {
      this.chartData.zones.forEach((zone) => {
        if (zone) {
          wholesomeData.push({ y: zone.startY });
          wholesomeData.push({ y: zone.endY });
        }
      });
    }

    this.chartData.lines.forEach((item) => {
      wholesomeData.push(...item.data);
    })

    // extract x min, max values
    if (type.x === 'date') {
      if (this.chartData.config.x.min) {
        domain.config.minX = new Date(this.chartData.config.x.min);
      } else {
        domain.config.minX = d3.min(wholesomeData, (item) => new Date(item.x));
      }
      if (this.chartData.config.x.max) {
        domain.config.maxX = new Date(this.chartData.config.x.max);
      } else {
        domain.config.maxX = d3.max(wholesomeData, (item) => new Date(item.x));
      }

      // add padding
      if (this.chartData.config.x.padding) {
        domain.config.minX = moment(domain.config.minX)
          .subtract(this.chartData.config.x.padding.size, (<any>this.chartData.config.x.padding.unit))
        domain.config.maxX = moment(domain.config.maxX)
          .add(this.chartData.config.x.padding.size, (<any>this.chartData.config.x.padding.unit))
      }

      domain.x = d3.scaleUtc()
        .domain([domain.config.minX, domain.config.maxX])
        .range([this.margins.left, this.width - this.margins.right]);
    } else {

      if (this.chartData.config.x.min) {
        domain.config.minX = this.chartData.config.x.min;
      } else {
        domain.config.minX = d3.min(wholesomeData, (item) => item.x);
      }
      if (this.chartData.config.x.max) {
        domain.config.maxX = this.chartData.config.x.max;
      } else {
        domain.config.maxX = d3.max(wholesomeData, (item) => item.x);
      }


      // add padding
      if (this.chartData.config.x.padding) {
        if (domain.config.minX - this.chartData.config.x.padding.size >= 0) {
          domain.config.minX = domain.config.minX - this.chartData.config.x.padding.size;
        } else {
          domain.config.minX = 0;
        }
        domain.config.maxX = domain.config.maxX + this.chartData.config.x.padding.size;
      }

      domain.x = d3.scalePoint()
        .domain([domain.config.minX, domain.config.maxX])
        .range([this.margins.left, this.width - this.margins.right]);
    }

    // extract y min, max values
    if (type.y === 'date') {
      if (this.chartData.config.y.min && this.chartData.config.y.max) {
        domain.config.minY = new Date(this.chartData.config.y.min);
        domain.config.maxY = new Date(this.chartData.config.y.max);
      } else {
        domain.config.minY = d3.min(wholesomeData, (item) => new Date(item.y));
        domain.config.maxY = d3.max(wholesomeData, (item) => new Date(item.y));
      }

      // add padding
      if (this.chartData.config.y.padding) {
        domain.config.minY = moment(domain.config.minY)
          .subtract(this.chartData.config.y.padding.size, (<any>this.chartData.config.y.padding.unit))
        domain.config.maxY = moment(domain.config.maxY)
          .add(this.chartData.config.y.padding.size, (<any>this.chartData.config.y.padding.unit))
      }
    } else {
      if (wholesomeData.length) {
        domain.config.minY = d3.min(wholesomeData, (item) => item.y);
        domain.config.maxY = d3.max(wholesomeData, (item) => item.y);
      } else {
        domain.config.minY = 0;
        domain.config.maxY = 1;
      }

      // Use explicit min/max as guaranteed minimum range, but expand if data exceeds
      if (this.chartData.config.y.min !== undefined && this.chartData.config.y.max !== undefined) {
        domain.config.minY = Math.min(domain.config.minY, Number(this.chartData.config.y.min));
        domain.config.maxY = Math.max(domain.config.maxY, Number(this.chartData.config.y.max));
      }

      // Add percentage-based padding to Y-axis (configurable via padding config, default 10%)
      const paddingPercent = this.chartData.config.y.padding?.size || 10;
      const range = domain.config.maxY - domain.config.minY;

      // Handle edge case where min equals max
      if (range === 0) {
        const buffer = Math.max(Math.abs(domain.config.minY) * (paddingPercent / 100), 1);
        domain.config.minY = domain.config.minY - buffer;
        domain.config.maxY = domain.config.maxY + buffer;
      } else if (paddingPercent > 0) {
        // Calculate buffer as percentage of the range
        const buffer = range * (paddingPercent / 100);
        domain.config.minY = domain.config.minY - buffer;
        domain.config.maxY = domain.config.maxY + buffer;
      }
    }

    domain.y = d3.scaleLinear()
      .domain([domain.config.minY, domain.config.maxY])
      .range([this.height - this.margins.bottom, this.margins.top])
      .nice();
    return domain;
  }

  ngOnDestroy() {
    this.resizeObserver.disconnect();
  }
}
